import { ChunkRefOut, CitationOut, SourceOut, WebSource, AppliedCorrection, PendingActionPromptInput } from '@/client';

/** Discriminator values for SSE events from the v2 assistant stream endpoint */
export const TURN_EVENT_KIND = {
	TURN_STARTED: 'turn_started',
	RETRIEVAL_READY: 'retrieval_ready',
	TEXT_DELTA: 'text_delta',
	STATUS: 'status',
	TURN_COMPLETED: 'turn_completed',
	TURN_ERROR: 'turn_error',
} as const;

export type TurnEventKind = (typeof TURN_EVENT_KIND)[keyof typeof TURN_EVENT_KIND];

export interface TurnStartedEvent {
	kind: typeof TURN_EVENT_KIND.TURN_STARTED;
	session_id: string;
	exchange_id: string;
}

export interface RetrievalReadyEvent {
	kind: typeof TURN_EVENT_KIND.RETRIEVAL_READY;
	session_id: string;
	exchange_id: string;
	chunk_refs: ChunkRefOut[];
}

export interface TextDeltaEvent {
	kind: typeof TURN_EVENT_KIND.TEXT_DELTA;
	session_id: string;
	delta: string;
}

/**
 * Multi-turn (component-family) progress label, e.g. "Checking this property's records…". Not
 * emitted on the ordinary RAG path — zero of these is the normal case. `phase`/`source_class`/
 * `iteration`/`stage` are optional enrichment (not consumed today; safe to ignore) — only `label`
 * is guaranteed to be renderable. See docs/local/chat-feature.md §B.
 */
export interface TurnStatusEvent {
	kind: typeof TURN_EVENT_KIND.STATUS;
	session_id: string;
	exchange_id: string;
	phase: 'planning' | 'source_check' | 'sufficiency' | 'fallback' | 'synthesis';
	label: string;
	source_class?: string | null;
	iteration?: number | null;
	stage?: number | null;
}

export interface TurnCompletedEvent {
	kind: typeof TURN_EVENT_KIND.TURN_COMPLETED;
	session_id: string;
	exchange_id: string;
	answer: string;
	chunk_refs: ChunkRefOut[];
	/** v1 citation contract (see AssistantQuery). Present when answer_format_version >= 2. */
	sources?: SourceOut[];
	citations?: CitationOut[];
	/** External pages cited on a web-fallback answer — the web analogue of chunk_refs. */
	web_sources?: WebSource[];
	/** Saved corrections/facts that fed this answer — the third evidence channel. */
	applied_corrections?: AppliedCorrection[];
	/** Set when the turn ended by asking (a correction offer or a which-one selection) instead of
	 * answering — null on a turn that answered normally. See docs/local/chat-feature.md §C2. */
	pending_action?: PendingActionPromptInput | null;
	answer_format_version?: number;
}

export interface TurnErrorEvent {
	kind: typeof TURN_EVENT_KIND.TURN_ERROR;
	session_id: string;
	error_code: string;
	message: string;
}

export type TurnStreamEvent =
	| TurnStartedEvent
	| RetrievalReadyEvent
	| TextDeltaEvent
	| TurnStatusEvent
	| TurnCompletedEvent
	| TurnErrorEvent;
